import React, {useEffect, useRef, useState} from "react";
import {Box, IconButton} from "@mui/joy";
import {XIcon} from "lucide-react";

type IkeepayCheckoutProps = {
    checkoutUrl?: string
    onSuccess?: () => void
    onClose?: () => void
    onError?: (message?: string) => void
}

/**
 * Ikeepay inline checkout. The iframe loads hidden and the overlay only shows once Ikeepay
 * posts "ikeepay-ready", so the user never sees a blank frame. "ikeepay-success" is only a UI
 * signal — the backend verifies the payment with Ikeepay before granting anything.
 */
const IkeepayCheckout = (props: IkeepayCheckoutProps) => {
    const iframeRef = useRef<HTMLIFrameElement>(null)
    const [ready, setReady] = useState(false)
    const {onSuccess, onClose, onError} = props

    useEffect(() => {
        setReady(false)
        if (!props.checkoutUrl)
            return

        let successTimeout: ReturnType<typeof setTimeout>
        const handleMessage = (event: MessageEvent) => {
            if (event.source !== iframeRef.current?.contentWindow)
                return

            const message = typeof event.data === "string" ? event.data : event.data?.type
            switch (message) {
                case "ikeepay-ready":
                    setReady(true)
                    break
                case "ikeepay-close":
                    onClose?.()
                    break
                case "ikeepay-success":
                    // Leave Ikeepay's own confirmation on screen briefly, as their integration does.
                    successTimeout = setTimeout(() => onSuccess?.(), 2000)
                    break
                case "ikeepay-error":
                    onError?.(event.data?.message)
                    break
            }
        }

        window.addEventListener("message", handleMessage)
        return () => {
            window.removeEventListener("message", handleMessage)
            clearTimeout(successTimeout)
        }
    }, [props.checkoutUrl, onSuccess, onClose, onError])

    if (!props.checkoutUrl)
        return null

    return <Box sx={{
        position: "fixed",
        inset: 0,
        zIndex: 1300,
        display: ready ? "flex" : "none",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(0, 0, 0, 0.6)",
        backdropFilter: "blur(4px)",
    }}>
        <Box sx={{
            position: "relative",
            width: "100%",
            maxWidth: 450,
            height: {xs: "100%", md: "85vh"},
            padding: {md: 2},
        }}>
            <IconButton variant={"soft"} color={"neutral"} onClick={onClose}
                        sx={{position: "absolute", top: {xs: 8, md: -40}, right: 16, zIndex: 1}}>
                <XIcon/>
            </IconButton>
            <Box component={"iframe"}
                 ref={iframeRef}
                 title={"Ikeepay"}
                 src={props.checkoutUrl}
                 allow={"payment"}
                 sx={{
                     width: "100%",
                     height: "100%",
                     border: "none",
                     background: "transparent",
                     borderRadius: {md: "2rem"},
                 }}/>
        </Box>
    </Box>
}

export default IkeepayCheckout
